// Shared review sections used by BOTH the Qualifying-Call detail view
// and the completed-Trailer review view in App.jsx.
//
// Exports:
//   - PillSwitcher          — 3-way segmented control
//   - Stat / Divider        — micro-components for the header stat strip
//   - TalkingTimeline       — Gong-style talk-time bar + clickable
//                             merged speaker chunks (seek-to-timestamp)
//   - TranscriptPanel       — searchable transcript with click-to-seek
//   - getSpeakerColor       — consistent per-speaker color across both views
//   - fmtTimecode           — "1:23" / "1:02:34"
//   - fmtDuration           — "12 min" / "1h 4m"
//   - computeSpeakerStats   — derive { totalSeconds, bySpeaker[] } from segments
//
// Both surfaces pass an `onSeek(seconds)` callback. The qualifying-call
// surface seeks the inline source-stream <video>; the trailer surface
// opens the full-recording modal at the timestamp instead (because the
// trailer player itself only contains the composed highlight reel,
// while talk-time data lives on the original recording's timeline).

import { useState, useMemo, useRef, useCallback, useEffect } from 'react';

// ─── Pill switcher ───────────────────────────────────────────────

export function PillSwitcher({ active, onChange, options }) {
  return (
    <div style={{
      display: 'inline-flex', alignSelf: 'flex-start',
      padding: 4, borderRadius: 14,
      background: 'var(--room-card-hover)', border: '1px solid var(--room-border)',
      gap: 2,
    }}>
      {options.map((opt) => {
        const isActive = active === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            style={{
              padding: '9px 16px', borderRadius: 10,
              border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
              background: isActive
                ? 'linear-gradient(135deg, #0891b2 0%, #0ea5e9 100%)'
                : 'transparent',
              color: isActive ? '#fff' : 'var(--room-text-body, #475569)',
              boxShadow: isActive ? '0 2px 8px rgba(8, 145, 178, 0.25)' : 'none',
              transition: 'all 0.15s ease',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {opt.emoji && <span aria-hidden="true">{opt.emoji}</span>}
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Stat / Divider micro-components ────────────────────────────

export function Stat({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--room-text-subtle)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--room-text-strong)' }}>
        {value}
      </div>
    </div>
  );
}

export function Divider() {
  return <div style={{ width: 1, background: 'var(--room-border, #e2e8f0)' }} />;
}

// ─── Speaker color palette + helpers ────────────────────────────

const SPEAKER_COLORS = [
  { bar: '#0891b2', soft: '#cffafe', text: '#0c4a6e' }, // cyan — Presenter
  { bar: '#9333ea', soft: '#f3e8ff', text: '#581c87' }, // purple — Prospect
  { bar: '#f59e0b', soft: '#fef3c7', text: '#92400e' }, // amber — 3rd
  { bar: '#10b981', soft: '#d1fae5', text: '#065f46' }, // emerald — 4th
  { bar: '#ef4444', soft: '#fee2e2', text: '#991b1b' }, // red — 5th+
];

export function getSpeakerColor(speakerId, fallbackIdx = 0) {
  if (speakerId == null) return SPEAKER_COLORS[fallbackIdx % SPEAKER_COLORS.length];
  const idx = typeof speakerId === 'number' ? speakerId : fallbackIdx;
  return SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
}

export function fmtTimecode(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '0:00';
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function fmtDuration(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m >= 1) return `${m} min`;
  return `${total}s`;
}

/**
 * Apply the layered speaker-name resolver to a built bySpeaker[] array.
 *
 * Priority (highest first):
 *   1. speakerNameOverrides[speakerId]   — user-supplied rename (always wins)
 *   2. (existing) multi-track participant name — already on sp.label
 *   3. demoAttendees email-match           — exact match on participant email
 *   4. demoAttendees rank-match            — when no email available
 *   5. calendarAttendees[idx].name         — rank-matched calendar invite
 *   6. extractedNames[speakerId].name      — Claude NER over the transcript
 *
 * Plus a TITLE lifting pass: demo_attendees.role becomes the speaker's
 * sublabel when we can match them by name or email — mimics the
 * email-signature title display in Gong/Chorus.
 *
 * The "generic test" gates lower-priority sources so they only fill
 * gaps — a multi-track real name from Recall is never overwritten by
 * an NER guess. Conversely, user overrides ALWAYS win because the user
 * is the ground truth.
 *
 * Returns a NEW array; safe to call inside useMemo.
 *
 * `sp.isOverride: true` is added when the user override wins so the UI
 * can render a subtle "renamed" affordance if needed.
 */
export function applySpeakerNameLayers(bySpeaker, sources) {
  if (!Array.isArray(bySpeaker) || !bySpeaker.length) return bySpeaker;
  const {
    speakerNameOverrides,
    calendarAttendees,
    extractedNames,
    demoAttendees,
  } = sources || {};

  // Matches names the system would have emitted as fallbacks. NEVER
  // overwrites anything else (multi-track real names, attendee names
  // already lifted up from email, etc).
  const GENERIC_RE = /^(Presenter|Prospect|Speaker [\d\w]+|Participant \d+)$/;

  // Build email + name lookup over demo_attendees once for fast
  // attribute-match passes below. Both keys are lowercased so case
  // mismatches (e.g. "Jordan Chen" in multi-track vs "jordan chen"
  // typed manually) don't miss.
  const attendeesByEmail = new Map();
  const attendeesByName = new Map();
  if (Array.isArray(demoAttendees)) {
    for (const a of demoAttendees) {
      if (!a) continue;
      if (a.email) attendeesByEmail.set(String(a.email).toLowerCase().trim(), a);
      if (a.name) attendeesByName.set(String(a.name).toLowerCase().trim(), a);
    }
  }

  return bySpeaker.map((sp, idx) => {
    let label = sp.label;
    let sublabel = sp.sublabel;
    let isOverride = false;

    const labelIsGeneric = !label || GENERIC_RE.test(label);

    // ── Layer 6: NER from transcript. Only fills generic gaps. ─────
    if (labelIsGeneric && extractedNames && typeof extractedNames === 'object') {
      const ner = extractedNames[String(sp.speaker)];
      if (ner && typeof ner.name === 'string' && ner.name.trim()) {
        label = ner.name.trim();
      }
    }

    // ── Layer 5: Calendar attendees rank-matched. Top talker ←→ first
    //     attendee, etc. Both lists are sorted by talk-time / attendee
    //     order respectively. Hardly ever wrong on 1:1s; on 4-person
    //     calls it's right 60-80% of the time. User override fixes the
    //     rest. ───────────────────────────────────────────────────
    if ((labelIsGeneric || !label) && Array.isArray(calendarAttendees) && calendarAttendees.length) {
      const attendee = calendarAttendees[idx];
      if (attendee && typeof attendee.name === 'string' && attendee.name.trim()) {
        label = attendee.name.trim();
        if (!sublabel && attendee.email) sublabel = attendee.email;
      }
    }

    // ── Layer 4: demo_attendees email-match. When the row has an
    //     email (multi-track lands one; diarized doesn't) and we have
    //     an attendee with the same email, lift that attendee's role
    //     into the sublabel + their name into the label (if generic).
    //     This is the "email signature" layer in spirit. ───────────
    if (sp.sublabel && typeof sp.sublabel === 'string' && sp.sublabel.includes('@')) {
      const match = attendeesByEmail.get(sp.sublabel.toLowerCase().trim());
      if (match) {
        if (match.role) sublabel = match.role; // promote role over raw email
        if (labelIsGeneric && match.name) label = match.name;
      }
    }

    // ── Layer 3: demo_attendees name-match. When the current label
    //     happens to align with an attendee name, lift the role into
    //     sublabel. Helps when multi-track produced the name but we
    //     didn't have a role to display. ───────────────────────────
    if (label && !sublabel) {
      const match = attendeesByName.get(String(label).toLowerCase().trim());
      if (match && match.role) sublabel = match.role;
    }

    // ── Layer 1 (highest): user override. Applied LAST so it always
    //     wins over multi-track + NER + calendar + attendees. ───────
    if (speakerNameOverrides && typeof speakerNameOverrides === 'object') {
      const override = speakerNameOverrides[String(sp.speaker)];
      if (override && typeof override === 'string' && override.trim()) {
        label = override.trim();
        isOverride = true;
        // If the override matches a known attendee name, lift role.
        if (!sublabel) {
          const match = attendeesByName.get(label.toLowerCase().trim());
          if (match && match.role) sublabel = match.role;
        }
      }
    }

    return { ...sp, label, sublabel: sublabel || null, isOverride };
  });
}

export function computeSpeakerStats(segments, labels) {
  const totals = new Map();
  const perSpeakerSegments = new Map();
  let total = 0;
  for (const s of segments) {
    if (s.speaker == null) continue;
    const dur = Math.max(0, (s.end || 0) - (s.start || 0));
    totals.set(s.speaker, (totals.get(s.speaker) || 0) + dur);
    if (!perSpeakerSegments.has(s.speaker)) perSpeakerSegments.set(s.speaker, []);
    perSpeakerSegments.get(s.speaker).push({ start: s.start, end: s.end });
    total += dur;
  }
  const bySpeaker = [...totals.entries()]
    .map(([speaker, seconds]) => ({
      speaker,
      seconds,
      pct: total > 0 ? Math.round((seconds / total) * 100) : 0,
      label: (labels && labels[speaker]) || `Speaker ${speaker}`,
      color: getSpeakerColor(speaker),
      segments: perSpeakerSegments.get(speaker) || [],
    }))
    .sort((a, b) => b.seconds - a.seconds);
  return { totalSeconds: total, bySpeaker };
}

// ─── Talking timeline ────────────────────────────────────────────
//
// Chorus/Gong-style visualization: one row per speaker, with a
// horizontal time-series bar showing exactly when they spoke across
// the full call duration. Each speaking segment becomes a thin
// vertical bar at its position on the timeline. Click any bar to
// seek the recording to that moment.
//
// Data shape on speakerStats.bySpeaker[]:
//   { speaker, label, sublabel?, pct, seconds, color, segments: [{start,end}] }
//
// Multi-track: parent passes participant.segments verbatim.
// Diarized fallback: computeSpeakerStats groups segments by speaker.

function getInitial(label) {
  if (!label) return '?';
  const trimmed = String(label).trim();
  if (!trimmed) return '?';
  return trimmed[0].toUpperCase();
}

// Refactored 2026-05-04 to a Gong-style scrub experience:
//   - Click ANYWHERE on the timeline (not just on speaker bars) to seek
//     to that timestamp. The whole track is a single hit-target,
//     speaker bars are decorative on top.
//   - Hover anywhere on the timeline shows a vertical "ghost" playhead
//     line + a tooltip pill with the timecode at the cursor — same
//     affordance as Gong/Chorus.
//   - Shared time axis spans the bottom of the panel rather than
//     floating above each row, so a hover at 6:42 lines up with the
//     6:42 marker visually instead of bouncing between rows.
//   - Speaker rows share one `<div>` ref so the hover line + click
//     target span all rows simultaneously — Gong's "scrub through the
//     conversation" feel rather than "scrub one speaker at a time."
//   - Visual polish: slightly taller bars, rounded corners, softer
//     track background gradient, larger avatars, refined typography.
export function TalkingTimeline({ speakerStats, onSeek, emptyMessage, onRename }) {
  const rows = (speakerStats && speakerStats.bySpeaker) || [];

  // Total call duration: use max segment.end across all speakers, not the
  // sum of talk-time (which excludes silence + cross-talk overlap).
  const totalDurationSeconds = useMemo(() => {
    let max = 0;
    for (const sp of rows) {
      for (const seg of (sp.segments || [])) {
        if (typeof seg.end === 'number' && seg.end > max) max = seg.end;
      }
    }
    return max;
  }, [rows]);

  // Hover state — fraction along the track [0,1] under the cursor.
  // Null when not hovering. Drives the ghost playhead + timecode pill.
  const [hoverFrac, setHoverFrac] = useState(null);
  const trackAreaRef = useRef(null);

  // Translate a mouse event to a fraction along the track. Used for
  // both click-to-seek and hover-preview. Reading the track wrapper's
  // bounding rect (not the row's) means the click area is the full
  // shared-track column regardless of which speaker row was hovered.
  const fracFromEvent = useCallback((e) => {
    const el = trackAreaRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const x = e.clientX - rect.left;
    return Math.min(1, Math.max(0, x / rect.width));
  }, []);

  const handleMouseMove = useCallback((e) => {
    const f = fracFromEvent(e);
    if (f == null) return;
    setHoverFrac(f);
  }, [fracFromEvent]);

  const handleMouseLeave = useCallback(() => setHoverFrac(null), []);

  const handleClick = useCallback((e) => {
    if (!onSeek) return;
    const f = fracFromEvent(e);
    if (f == null) return;
    onSeek(f * totalDurationSeconds);
  }, [fracFromEvent, onSeek, totalDurationSeconds]);

  if (!rows.length || totalDurationSeconds <= 0) {
    return (
      <div style={{
        background: 'var(--room-card)', borderRadius: 14, padding: 28,
        border: '1px solid var(--room-border)', textAlign: 'center', color: 'var(--room-text-muted)', fontSize: 14,
      }}>
        {emptyMessage || "No speaker timeline — diarization data wasn't available for this recording."}
      </div>
    );
  }

  // Layout column widths used for both the speaker rows and the
  // shared time axis. Defining them once keeps the axis ticks
  // perfectly aligned with the rows above.
  const COL_IDENTITY = 240;   // avatar + name column
  const COL_PCT = 60;         // trailing percentage column
  const PCT_GAP = 20;         // visual breathing room before %

  // The hovered timecode pill — only render when actually hovering.
  const hoverTimecode = hoverFrac != null
    ? fmtTimecode(hoverFrac * totalDurationSeconds)
    : null;

  return (
    <div style={{
      background: 'var(--room-card)', borderRadius: 14, padding: 24,
      border: '1px solid var(--room-border)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 4, gap: 12, flexWrap: 'wrap',
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong)', letterSpacing: '-0.01em' }}>
          Talk-time
        </h3>
        <span style={{ fontSize: 12, color: 'var(--room-text-subtle)', fontVariantNumeric: 'tabular-nums' }}>
          Total call · {fmtDuration(totalDurationSeconds)}
        </span>
      </div>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--room-text-muted)' }}>
        Click anywhere on the timeline to jump to that moment.
      </p>

      {/* The whole timeline is one click-target. We position the hover
          line + cursor pill absolutely over a wrapper that contains
          all speaker rows + the shared time axis, so hovering /
          clicking is a single fluid interaction across rows. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `${COL_IDENTITY}px 1fr ${COL_PCT}px`,
        gap: PCT_GAP,
        position: 'relative',
        // The shared track area spans the middle column only — that's
        // where mouse events should hit. We give it a ref via a sibling
        // overlay grid below.
      }}>
        {/* Speaker rows */}
        {rows.map((sp, idx) => (
          <SpeakerRow
            key={sp.speaker}
            sp={sp}
            totalDurationSeconds={totalDurationSeconds}
            isFirst={idx === 0}
            isLast={idx === rows.length - 1}
            onRename={onRename}
          />
        ))}

        {/* Time axis — sits in the middle column, below the rows. */}
        <div style={{ gridColumn: '1' }} />
        <div style={{
          gridColumn: '2',
          position: 'relative', height: 18, marginTop: 6,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {/* 3 ticks (start / middle / end) instead of 5 — at the
              widths the timeline lives at inside the 2-col detail
              layout, 5 ticks collide and the labels render as a
              garbled run-on. 3 keeps each label readable while still
              giving the eye a sense of scale. */}
          {[0, 0.5, 1].map((frac) => (
            <span
              key={frac}
              style={{
                position: 'absolute', left: `${frac * 100}%`,
                transform: frac === 0 ? 'translateX(0)'
                  : frac === 1 ? 'translateX(-100%)'
                  : 'translateX(-50%)',
                top: 0,
                fontSize: 11, color: 'var(--room-text-subtle)', fontWeight: 500,
              }}
            >
              {fmtTimecode(totalDurationSeconds * frac)}
            </span>
          ))}
        </div>
        <div style={{ gridColumn: '3' }} />

        {/* Click + hover overlay — covers ONLY the middle column
            (the timeline area, not the avatars or % column). All
            rows share this single overlay so the seekable area is
            the entire stacked-row track height, like Gong. */}
        <div
          ref={trackAreaRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          style={{
            position: 'absolute',
            // Span across only the middle column (track area).
            left: COL_IDENTITY + PCT_GAP,
            right: COL_PCT + PCT_GAP,
            top: 0,
            // Stop above the time axis (height 18 + marginTop 6).
            bottom: 24,
            cursor: onSeek ? 'pointer' : 'default',
            // Layered above the speaker bars (which use pointer-events: none).
            zIndex: 2,
          }}
          aria-label="Talk-time timeline. Click to jump to a moment."
        >
          {/* Ghost playhead — vertical line at the cursor position. */}
          {hoverFrac != null && (
            <div style={{
              position: 'absolute',
              left: `${hoverFrac * 100}%`,
              top: 0, bottom: 0,
              width: 2,
              background: 'rgba(15, 23, 42, 0.55)',
              borderRadius: 1,
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
              boxShadow: '0 0 0 1px rgba(255,255,255,0.6)',
            }} />
          )}
          {/* Timecode pill — floats above the ghost playhead. Clamps
              to the edges so it doesn't get clipped at 0:00 / endpoint. */}
          {hoverTimecode != null && (
            <div style={{
              position: 'absolute',
              left: `${hoverFrac * 100}%`,
              top: -28,
              transform: hoverFrac < 0.05 ? 'translateX(0)'
                : hoverFrac > 0.95 ? 'translateX(-100%)'
                : 'translateX(-50%)',
              padding: '4px 8px',
              background: 'var(--room-text-strong, #0f172a)', color: '#fff',
              fontSize: 11, fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              borderRadius: 6,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 14px rgba(15, 23, 42, 0.25)',
            }}>
              {hoverTimecode}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SpeakerRow({ sp, totalDurationSeconds, isFirst, isLast, onRename }) {
  const segments = sp.segments || [];
  // Inline rename state. `editing` toggles the label between display
  // mode and an <input>; `draft` holds the in-flight value before the
  // user confirms with Enter / blur. Both reset when the row's `sp.label`
  // changes externally (e.g. a refetch lands new names).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Auto-focus when entering edit mode. Without this the user has to
  // tab/click into the input — extra friction for a 2-second action.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = async () => {
    if (saving) return;
    const trimmed = (draft || '').trim();
    // No-op if unchanged. Saves a network round-trip + audit-log noise.
    if (trimmed === (sp.label || '') || (!trimmed && !sp.isOverride)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      // Pass `null` (not empty string) to clear, so the backend
      // delete-and-shrink path runs cleanly.
      await onRename(sp.speaker, trimmed || null);
      setEditing(false);
    } catch (err) {
      // Surface the error inline so the user can retry. Reverting the
      // optimistic UI in the parent is the caller's job; we just keep
      // edit-mode open.
      console.warn('[CallReviewSections] rename failed:', err.message);
      alert(`Couldn't save: ${err.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft('');
    setEditing(false);
  };

  return (
    <>
      {/* Identity column — larger avatar, refined typography. */}
      <div style={{
        gridColumn: '1',
        display: 'flex', alignItems: 'center', gap: 12, minWidth: 0,
        paddingTop: isFirst ? 0 : 10,
        paddingBottom: isLast ? 0 : 10,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: `linear-gradient(135deg, ${sp.color.bar} 0%, ${shadeColor(sp.color.bar, -15)} 100%)`,
          color: '#fff', fontSize: 14, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 2px 8px ${withAlpha(sp.color.bar, 0.25)}`,
          letterSpacing: '-0.01em',
        }}>
          {getInitial(sp.label)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          {editing ? (
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                else if (e.key === 'Escape') cancel();
              }}
              disabled={saving}
              maxLength={120}
              placeholder={sp.label}
              style={{
                fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)',
                letterSpacing: '-0.01em',
                background: 'var(--room-card, #fff)',
                border: '1px solid var(--room-border)',
                borderRadius: 6,
                padding: '4px 8px',
                width: '100%',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              aria-label="Speaker name"
            />
          ) : (
            <div
              onDoubleClick={onRename ? () => { setDraft(sp.label || ''); setEditing(true); } : undefined}
              style={{
                fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                letterSpacing: '-0.01em',
                display: 'flex', alignItems: 'center', gap: 6,
                cursor: onRename ? 'text' : 'default',
              }}
              title={onRename ? 'Double-click to rename' : undefined}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {sp.label}
              </span>
              {onRename && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setDraft(sp.label || ''); setEditing(true); }}
                  aria-label={`Rename ${sp.label}`}
                  title="Rename speaker"
                  style={{
                    background: 'none', border: 'none', padding: 2,
                    cursor: 'pointer', borderRadius: 4,
                    color: 'var(--room-text-subtle)',
                    display: 'inline-flex', alignItems: 'center',
                    opacity: 0.5,
                    transition: 'opacity 0.15s, background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'var(--room-card-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.background = 'none'; }}
                >
                  {/* Pencil icon — 14×14, currentColor */}
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M11.5 1.5l3 3-9 9H2.5v-3l9-9z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          )}
          {sp.sublabel && !editing && (
            <div style={{
              fontSize: 11, color: 'var(--room-text-subtle)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              marginTop: 1,
            }}>
              {sp.sublabel}
            </div>
          )}
        </div>
      </div>

      {/* Timeline track — gradient surface, rounded bars. The bars
          are decorative; the click hit-target is the overlay above. */}
      <div style={{
        gridColumn: '2',
        position: 'relative', height: 44,
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: 10,
        border: '1px solid var(--room-border)',
        overflow: 'hidden',
        marginTop: isFirst ? 0 : 6,
        marginBottom: isLast ? 0 : 6,
      }}>
        {segments.map((seg, i) => {
          if (typeof seg.start !== 'number' || typeof seg.end !== 'number') return null;
          const left = (seg.start / totalDurationSeconds) * 100;
          // Minimum-visible width — single-utterance segments would
          // otherwise be sub-pixel and disappear. 0.3% ≈ 3px on a
          // 1000px-wide track.
          const widthFrac = Math.max(0.003, (seg.end - seg.start) / totalDurationSeconds);
          const width = widthFrac * 100;
          return (
            <div
              key={i}
              title={`${fmtTimecode(seg.start)} — ${fmtTimecode(seg.end)}`}
              style={{
                position: 'absolute',
                left: `${left}%`, width: `${width}%`,
                top: 6, bottom: 6,
                background: `linear-gradient(180deg, ${sp.color.bar} 0%, ${shadeColor(sp.color.bar, -10)} 100%)`,
                borderRadius: 4,
                opacity: 0.92,
                // Click goes through to the overlay above so the user
                // can scrub freely without having to hit a tick exactly.
                pointerEvents: 'none',
                boxShadow: `0 1px 2px ${withAlpha(sp.color.bar, 0.3)}`,
              }}
            />
          );
        })}
      </div>

      {/* Percentage column. */}
      <div style={{
        gridColumn: '3',
        textAlign: 'right',
        fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong)',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.02em',
        alignSelf: 'center',
      }}>
        {sp.pct}%
      </div>
    </>
  );
}

// ─── Color utilities ─────────────────────────────────────────────
//
// shadeColor: lighten / darken a hex color by `percent` (-100..100).
// Used for the bar gradient (top-to-bottom subtle shading) + avatar
// gradient. Inline rather than via CSS filter so the boxShadow can
// reference the original color.
function shadeColor(hex, percent) {
  if (!hex || hex[0] !== '#') return hex;
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const amt = Math.round(2.55 * percent);
  r = Math.max(0, Math.min(255, r + amt));
  g = Math.max(0, Math.min(255, g + amt));
  b = Math.max(0, Math.min(255, b + amt));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// withAlpha: hex → rgba() with given alpha. Used for soft shadows
// keyed off each speaker's accent color.
function withAlpha(hex, alpha) {
  if (!hex || hex[0] !== '#') return hex;
  const num = parseInt(hex.slice(1), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ─── Transcript with search ──────────────────────────────────────

// Highlight every occurrence of `query` (case-insensitive) inside `text`.
function highlightMatches(text, query) {
  if (!query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const parts = [];
  let i = 0;
  while (i < text.length) {
    const found = lower.indexOf(q, i);
    if (found === -1) {
      parts.push(text.slice(i));
      break;
    }
    if (found > i) parts.push(text.slice(i, found));
    parts.push(
      <mark key={`m${found}`} style={{
        background: '#fef3c7', color: '#78350f',
        padding: '0 2px', borderRadius: 3,
      }}>
        {text.slice(found, found + q.length)}
      </mark>
    );
    i = found + q.length;
  }
  return parts;
}

export function TranscriptPanel({ transcript, onSeek }) {
  const [query, setQuery] = useState('');
  const [copyState, setCopyState] = useState('idle');  // 'idle' | 'copied' | 'failed'
  const segments = Array.isArray(transcript?.segments) ? transcript.segments : null;
  const labels = (transcript && transcript.speakerLabels) || {};

  // Build a clean Speaker: text\nSpeaker: text version of the
  // transcript for the Copy button. If we only have a raw string
  // (no segments), fall back to that. Headers + spacing match the
  // format the rendered panel uses so what you copy looks like
  // what you saw.
  const buildCopyableText = () => {
    if (segments && segments.length) {
      return segments.map((s) => {
        const label = labels[s.speaker] || (s.speaker != null ? `Speaker ${s.speaker}` : 'Speaker');
        return `${label}: ${(s.text || '').trim()}`;
      }).join('\n');
    }
    return typeof transcript === 'string' ? transcript : JSON.stringify(transcript, null, 2);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildCopyableText());
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch (_) {
      setCopyState('failed');
      setTimeout(() => setCopyState('idle'), 2400);
    }
  };

  const trimmedQuery = query.trim();
  const filteredSegments = useMemo(() => {
    if (!segments) return null;
    if (!trimmedQuery) return segments;
    const q = trimmedQuery.toLowerCase();
    return segments.filter((s) => (s.text || '').toLowerCase().includes(q));
  }, [segments, trimmedQuery]);

  const matchCount = trimmedQuery && filteredSegments ? filteredSegments.length : 0;

  return (
    <div style={{
      background: 'var(--room-card)', borderRadius: 14, padding: 24,
      border: '1px solid var(--room-border)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, marginBottom: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong)' }}>
            Transcript
          </h3>
          {(segments?.length || typeof transcript === 'string') && (
            <button
              type="button"
              onClick={handleCopy}
              title="Copy the full transcript to your clipboard"
              style={{
                padding: '5px 10px', borderRadius: 8,
                border: '1px solid var(--room-border)',
                background: copyState === 'copied' ? '#d1fae5' : '#fff',
                color: copyState === 'copied' ? '#065f46' : 'var(--room-text-body, #475569)',
                fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {copyState === 'copied'
                ? '✓ Copied'
                : copyState === 'failed'
                ? 'Copy failed'
                : 'Copy transcript'}
            </button>
          )}
        </div>
        <div style={{
          position: 'relative', display: 'flex', alignItems: 'center',
          flex: '1 1 280px', maxWidth: 420,
        }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%',
            transform: 'translateY(-50%)', color: 'var(--room-text-subtle)',
            fontSize: 14, pointerEvents: 'none',
          }}>
            🔍
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the transcript…"
            style={{
              width: '100%', padding: '9px 12px 9px 36px',
              border: '1px solid var(--room-border)', borderRadius: 10,
              fontSize: 13, fontFamily: 'inherit', color: 'var(--room-text-strong)',
              background: 'var(--room-surface)',
              outline: 'none',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#0891b2'; e.currentTarget.style.background = 'var(--room-card, #fff)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--room-border, #e2e8f0)'; e.currentTarget.style.background = 'var(--room-surface, #f8fafc)'; }}
          />
          {trimmedQuery && (
            <button
              type="button"
              onClick={() => setQuery('')}
              title="Clear search"
              style={{
                position: 'absolute', right: 8, top: '50%',
                transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--room-text-muted)', fontSize: 13, padding: 4,
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {trimmedQuery && (
        <div style={{ fontSize: 12, color: 'var(--room-text-muted)', marginBottom: 10 }}>
          {matchCount === 0
            ? <>No matches for <strong style={{ color: 'var(--room-text-strong)' }}>"{trimmedQuery}"</strong>.</>
            : <>{matchCount} {matchCount === 1 ? 'segment matches' : 'segments match'} <strong style={{ color: 'var(--room-text-strong)' }}>"{trimmedQuery}"</strong>.</>
          }
        </div>
      )}

      <div style={{
        padding: 16, background: 'var(--room-surface)',
        borderRadius: 10, maxHeight: 520, overflow: 'auto',
        fontSize: 13, color: 'var(--room-text-body)', lineHeight: 1.6,
        border: '1px solid var(--room-border-soft)',
      }}>
        {segments ? (
          (filteredSegments || []).length === 0 && trimmedQuery ? (
            <div style={{ color: 'var(--room-text-subtle)', fontStyle: 'italic' }}>
              Try a different word or phrase.
            </div>
          ) : (filteredSegments || []).map((s, i) => {
            const color = getSpeakerColor(s.speaker);
            const label = labels[s.speaker] || (s.speaker != null ? `Speaker ${s.speaker}` : 'Speaker');
            return (
              <div
                key={i}
                onClick={() => onSeek && typeof s.start === 'number' && onSeek(s.start)}
                style={{
                  display: 'grid', gridTemplateColumns: '60px 100px 1fr',
                  gap: 10, alignItems: 'start',
                  padding: '6px 8px', borderRadius: 6,
                  cursor: onSeek ? 'pointer' : 'default',
                  marginBottom: 2,
                  transition: 'background 0.1s ease',
                }}
                onMouseEnter={(e) => { if (onSeek) e.currentTarget.style.background = 'var(--room-card, #fff)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--room-text-subtle)',
                  fontVariantNumeric: 'tabular-nums', paddingTop: 2,
                }}>
                  {fmtTimecode(s.start)}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.03em',
                  color: color.text, background: color.soft,
                  padding: '2px 8px', borderRadius: 100,
                  alignSelf: 'start', justifySelf: 'start',
                }}>
                  {label}
                </span>
                <span style={{ color: 'var(--room-text-body)' }}>
                  {highlightMatches(s.text || '', trimmedQuery)}
                </span>
              </div>
            );
          })
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {typeof transcript === 'string' ? transcript : JSON.stringify(transcript, null, 2)}
          </div>
        )}
      </div>
    </div>
  );
}
